import type { ReactNode } from 'react';
import { Badge, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import type { settings } from '@repletoryreach/sdk-api';
import { CopyableId } from '../../../../components/CopyableId';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import { formatDateTime, formatInteger, formatRelative, humanBytes } from '../../../../lib/format';
import type { Indexed } from '../../../../lib/types';
import { useT } from '../../../../i18n/I18nProvider';
import { VerifiedBadge } from './VerifiedBadge';

const MUTED = 'var(--rr-color-text-muted)';

function Field({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <Group justify="space-between" align="baseline" wrap="nowrap" gap="md">
      <Text role="caption" c={MUTED}>{label}</Text>
      <div style={{ textAlign: 'right', minWidth: 0 }}>{children}</div>
    </Group>
  );
}

/**
 * What a snapshot actually contains, one row per database.
 *
 * Everything here is already in the list response: the manifest is projected
 * whole, so there is no second read. `present` is the column that matters,
 * because a manifest naming a dump the store does not hold is the difference
 * between a backup and the belief that there is one.
 */
export function SnapshotDetailDrawer({ snapshot, verification, onClose }: {
  snapshot: settings.BackupSnapshot | null;
  verification: settings.BackupListMeta['verification'] | undefined;
  onClose: () => void;
}) {
  const t = useT();

  const rows = (snapshot?.databases ?? []) as Indexed<settings.BackupDatabase>[];

  return (
    <Drawer opened={snapshot !== null} onClose={onClose} title={t('system.backups.detail.title')} size="xl">
      {snapshot && (
        <Stack gap="lg">
          <Stack gap="xs">
            <Group gap="sm" align="center" wrap="nowrap">
              <Title order={5}><code>{snapshot.id}</code></Title>
              <VerifiedBadge snapshot={snapshot} />
            </Group>
            <Text role="caption" c={MUTED}>
              {verification === 'none'
                ? t('system.backups.verified.explainNone')
                : t('system.backups.verified.explain')}
            </Text>
          </Stack>

          <Stack gap="xs">
            <Field label={t('system.backups.detail.taken')}>
              {snapshot.taken_at
                ? <TwoLineCell primary={formatDateTime(snapshot.taken_at)} secondary={formatRelative(snapshot.taken_at)} />
                : <Text role="bodySmall" c={MUTED}>{t('common.unknown')}</Text>}
            </Field>
            <Field label={t('system.backups.detail.host')}>
              {snapshot.host ? <Text role="bodySmall">{snapshot.host}</Text> : null}
            </Field>
            <Field label={t('system.backups.detail.container')}>
              {snapshot.container ? <Text role="bodySmall">{snapshot.container}</Text> : null}
            </Field>
            {/* The location is the argument to restore.sh, so it is quoted
                verbatim rather than summarised. */}
            <Field label={t('system.backups.detail.location')}>
              <code style={{ wordBreak: 'break-all' }}>{snapshot.location}</code>
            </Field>
            <Field label={t('system.backups.detail.encryption')}>
              <Badge size="xs" variant="light" color={snapshot.encryption === 'none' ? 'gray' : 'green'}>
                {snapshot.encryption === 'none' ? t('system.backups.encryption.none') : snapshot.encryption}
              </Badge>
            </Field>
            <Field label={t('system.backups.detail.totalSize')}>
              <Text role="bodySmall">{humanBytes(snapshot.total_bytes)}</Text>
            </Field>
            <Field label={t('system.backups.detail.liveRows')}>
              {snapshot.total_live_rows != null
                ? <Text role="bodySmall">{formatInteger(snapshot.total_live_rows)}</Text>
                : null}
            </Field>
          </Stack>

          <Text role="caption" c={MUTED}>{t('system.backups.detail.restoreHint')}</Text>

          <Stack gap="sm">
            <Title order={6}>{t('system.backups.detail.databases')}</Title>
            <DataTable<Indexed<settings.BackupDatabase>>
              records={rows}
              idAccessor="database"
              columns={[
                {
                  accessor: 'database',
                  title: t('system.backups.detail.columns.database'),
                  render: (d) => <TwoLineCell primary={d.database} secondary={d.file ?? undefined} />
                },
                {
                  accessor: 'bytes',
                  title: t('system.backups.detail.columns.size'),
                  textAlign: 'right',
                  // An encrypted copy is larger than the dump it came from, so
                  // the stored size is shown whenever the two disagree instead
                  // of quietly presenting one as the other.
                  render: (d) => (
                    <TwoLineCell
                      primary={humanBytes(d.bytes)}
                      secondary={
                        d.stored_bytes != null && d.stored_bytes !== d.bytes
                          ? t('system.backups.detail.storedDiffers', { bytes: humanBytes(d.stored_bytes) })
                          : undefined
                      }
                    />
                  )
                },
                {
                  accessor: 'live_rows',
                  title: t('system.backups.detail.columns.rows'),
                  textAlign: 'right',
                  render: (d) => d.live_rows != null
                    ? formatInteger(d.live_rows)
                    : <Text role="caption" c={MUTED}>{t('common.unknown')}</Text>
                },
                {
                  accessor: 'sha256',
                  title: t('system.backups.detail.columns.checksum'),
                  render: (d) => d.sha256
                    ? <CopyableId value={d.sha256} label={d.sha256} chars={12} />
                    : <Text role="caption" c={MUTED}>{t('common.none')}</Text>
                },
                {
                  accessor: 'present',
                  title: t('system.backups.detail.columns.present'),
                  render: (d) => {
                    if (d.present === true) {
                      return <Badge size="xs" variant="light" color="green">{t('system.backups.detail.present.yes')}</Badge>;
                    }
                    if (d.present === false) {
                      return <Badge size="xs" variant="light" color="red">{t('system.backups.detail.present.no')}</Badge>;
                    }
                    // Neither present nor missing: the store was never listed,
                    // and an unanswered question must not read as a pass.
                    return <Badge size="xs" variant="light" color="gray">{t('system.backups.detail.present.unknown')}</Badge>;
                  }
                }
              ]}
            />
            <Text role="caption" c={MUTED}>{t('system.backups.detail.checksumHint')}</Text>
          </Stack>

          {snapshot.extra && Object.keys(snapshot.extra).length > 0 && (
            <Stack gap="xs">
              <Title order={6}>{t('system.backups.detail.extra')}</Title>
              <pre className="rr-command-block">{JSON.stringify(snapshot.extra, null, 2)}</pre>
            </Stack>
          )}
        </Stack>
      )}
    </Drawer>
  );
}
